import { OnboardingFlow } from '@mondaygpt/constants'
import takaLogo from 'assets/taka-logo-icon.svg'
import { ArrowLeftRight, Instagram } from 'lucide-react'
import React from 'react'
import { FlowConfig, FlowConfigRegistry } from './flow-config.types'

// ============================================================================
// SHARED ASSETS
// ============================================================================

export const TAKA_LOGO_URL = takaLogo

const SOCIAL_HERO_IMAGE =
  'https://dapulse-res.cloudinary.com/image/upload/v1765975087/taka/landing-page/social/hero.png'

// ============================================================================
// REUSABLE COMPONENTS
// ============================================================================

/** Taka logo only (used for default flow) */
const TakaLogo: React.FC = () => (
  <img src={TAKA_LOGO_URL} alt="Taka" className="h-14 w-14 rounded-2xl shadow-lg" />
)

/** Taka + Partner logo with connection arrows */
interface PartnerLogoProps {
  partnerLogo: React.ReactNode
}

const TakaPartnerLogo: React.FC<PartnerLogoProps> = ({ partnerLogo }) => (
  <div className="flex items-center justify-center gap-3">
    <img src={TAKA_LOGO_URL} alt="Taka" className="h-14 w-14 rounded-2xl shadow-lg" />
    <div className="flex items-center justify-center text-muted-foreground">
      <ArrowLeftRight size={24} strokeWidth={2} />
    </div>
    {partnerLogo}
  </div>
)

/** Instagram partner logo */
const InstagramLogo: React.FC = () => (
  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#F77737] shadow-lg">
    <Instagram size={28} className="text-white" strokeWidth={2} />
  </div>
)

// ============================================================================
// HERO CONTENT COMPONENTS
// ============================================================================

/** Default hero: Instagram mockup image (social is the product). */
const DefaultHeroContent: React.FC = () => (
  <div className="relative w-full">
    <img
      src={SOCIAL_HERO_IMAGE}
      alt="Instagram posts created by Instella AI"
      className="max-h-[70vh] w-full object-contain"
    />
  </div>
)

/** Social hero: Instagram mockup image */
const SocialHeroContent: React.FC = () => (
  <div className="relative w-full">
    <img
      src={SOCIAL_HERO_IMAGE}
      alt="Instagram posts created by Instella AI"
      className="max-h-[70vh] w-full object-contain"
    />
  </div>
)

// ============================================================================
// FLOW CONFIGURATIONS
// ============================================================================

/**
 * Creates a guarded fallback component that shows an error message if rendered
 * before the real component is initialized via setOnboardingComponent().
 *
 * This prevents silent blank screens if initialization fails.
 */
const createGuardedComponent =
  (name: string): React.FC =>
  () => {
    console.error(
      `${name} was rendered before initialization. This usually means setOnboardingComponent() was not called.`
    )
    return (
      <div className="flex h-screen items-center justify-center">
        <p className="text-gray-600">Something went wrong. Please refresh the page.</p>
      </div>
    )
  }

// Onboarding component initialized with a guarded fallback to avoid silent blank
// screens. Wired lazily via `setOnboardingComponent` so the onboarding module
// can register itself without a circular import. All flows render the same
// surface — per-flow content variations live inside the component's flow-chrome
// lookup, so a single ref is enough.
let DarkOnboardingFlow: React.ComponentType = createGuardedComponent('DarkOnboardingFlow')

export const setOnboardingComponent = (component: React.ComponentType) => {
  DarkOnboardingFlow = component
}

/**
 * Default flow configuration.
 * Used when users come from the home page or other non-specific sources.
 */
const defaultFlowConfig: FlowConfig = {
  id: OnboardingFlow.Default,
  landingPagePath: null,

  login: {
    form: {
      logoSection: <TakaLogo />,
      heading: 'Run your business smarter',
      subheading:
        'AI-powered apps that handle the hard and boring tasks - so you can focus on what matters.',
      buttonText: 'Continue',
    },
    layout: {
      gradient: 'from-[color:var(--acid)] via-[color:var(--acid-tint)] to-[color:var(--acid-text)]',
      heroContent: <DefaultHeroContent />,
      heroHeadline: 'AI apps that do the work\nof running your business',
      heroSubheadline:
        "Handle the tasks you don't have time for - so you can focus on what you do best.",
    },
  },

  onboarding: {
    get component() {
      return DarkOnboardingFlow
    },
    internalBusinessCreation: true,
  },
}

/**
 * Social/Instagram flow configuration.
 * Used when users come from /lp/social landing page (Instella).
 */
const socialFlowConfig: FlowConfig = {
  id: OnboardingFlow.Social,
  landingPagePath: '/lp/social',

  login: {
    form: {
      logoSection: <TakaPartnerLogo partnerLogo={<InstagramLogo />} />,
      heading: 'Create Instagram posts in seconds',
      subheading: 'Professional, on-brand posts for your business. No design skills needed.',
      buttonText: 'Continue',
    },
    layout: {
      gradient: 'from-[color:var(--acid)] via-[color:var(--acid-tint)] to-[color:var(--acid)]',
      heroContent: <SocialHeroContent />,
      heroHeadline: 'Create, Manage, and Shine on Instagram',
      heroSubheadline: 'Your Brand. Captivating Posts. Instant AI Magic',
    },
  },

  onboarding: {
    get component() {
      return DarkOnboardingFlow
    },
    internalBusinessCreation: true,
  },
}

// ============================================================================
// FLOW REGISTRY
// ============================================================================

/**
 * Registry of all available flow configurations.
 *
 * To add a new flow:
 * 1. Add the flow ID to OnboardingFlow enum in @mondaygpt/constants
 * 2. Create the flow config above with login and onboarding settings
 * 3. Register it in this object
 */
export const FLOW_CONFIGS: FlowConfigRegistry = {
  [OnboardingFlow.Default]: defaultFlowConfig,
  [OnboardingFlow.Social]: socialFlowConfig,
}

/**
 * Get the flow configuration for a given flow ID.
 * Falls back to default flow if the flow ID is not found, with a warning.
 */
export const getFlowConfig = (flowId: OnboardingFlow | undefined): FlowConfig => {
  if (!flowId) {
    return FLOW_CONFIGS[OnboardingFlow.Default]
  }

  const config = FLOW_CONFIGS[flowId]
  if (!config) {
    console.warn(`Flow config not found for "${flowId}", using default flow`)
    return FLOW_CONFIGS[OnboardingFlow.Default]
  }

  return config
}
